import { memo, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'

// Actions
import { actions as audioActions } from 'modules/audio/actions'
import { actions as authActions } from 'modules/auth/actions'

// Components
import { WaveForm } from 'kit'

// Hooks
import { useAudioPlayStates, useCurrentPos } from 'modules/audio/hooks'

// Selectors
import { selectLoopBarPositions } from 'modules/audio/selectors'

// Types
import { Loop, State, Track } from 'types'

type WaveformProps = {
  track: Track
  loop?: Loop | null | undefined
  contextKey: string
  songItemId: string
  isHovered?: boolean
  className: string
  showOnSmall?: boolean
}

const Waveform = ({ track, loop, contextKey, songItemId, isHovered, className, showOnSmall }: WaveformProps): JSX.Element => {
  const dispatch = useDispatch()
  const { isActive, isPlaying, isLoading, isLoadingLoop } = useAudioPlayStates(songItemId, contextKey)
  const currentPos = useCurrentPos(50)

  const state = useSelector<State, State>(s => s)
  const { audio } = state
  const trackId = track.id
  const barPositions = selectLoopBarPositions(state, trackId)
  const beatsPerBar = audio.features[String(trackId)]?.beats_per_bar
  const { isHotkeySeeking } = audio
  const trackLength = track.length
  const useTrackPreview = !state.auth.isAuthenticated

  const handleseekToFactor = useCallback(
    (factor: number) => {
      if (!isPlaying && (!isLoading || !isLoadingLoop)) {
        if (isActive) {
          dispatch(audioActions.togglePaused())
        } else if (trackId) {
          dispatch(audioActions.playTrack(songItemId, trackId, contextKey, true, loop))
        }
      }
      dispatch(audioActions.seekToTime(factor * trackLength))
    },
    [isPlaying, isLoadingLoop, isActive, dispatch, contextKey, isLoading, loop, trackId, songItemId, trackLength],
  )

  return (
    <WaveForm
      trackId={track.id}
      points={track.waveformjs}
      loop={loop}
      onClick={handleseekToFactor}
      onClickPreview={() => dispatch(authActions.showSignupModal('play'))}
      duration={trackLength}
      currentPos={currentPos}
      isActive={isActive}
      className={className}
      barPositions={barPositions}
      beatsPerBar={beatsPerBar}
      isHovered={isHovered}
      isHotkeySeeking={isHotkeySeeking}
      setIsHotkeySeeking={() => dispatch(audioActions.setIsHotkeySeeking)}
      useTrackPreview={useTrackPreview}
      showOnSmall={showOnSmall}
    />
  )
}

export default memo<WaveformProps>(Waveform)
